import React, { useState, useEffect } from "react";
import { gql, useMutation } from "@apollo/client";
import { Modal, TextInput, Select, SelectItem } from "@carbon/react";

const EDIT_TIMEPLAN_CUSTOMIZATION = gql`
    mutation EditTimeplanCustomization($customizationId: String!, $fromUnit: Int, $toUnit: Int, $content: String, $dayId: Int) {
        editTimeplanCustomization(customizationId: $customizationId, fromUnit: $fromUnit, toUnit: $toUnit, content: $content, dayId: $dayId) {
            id
            fromUnit
            toUnit
            content
            dayId
        }
    }
`;

const REMOVE_TIMEPLAN_CUSTOMIZATION = gql`
    mutation RemoveTimeplanCustomization($customizationId: String!) {
        removeTimeplanCustomization(customizationId: $customizationId)
    }
`;

export default function PersonalizationEditModal({ customization, studentId, timeDescriptions, dayNames = [], onClose }) {
    const [content, setContent] = useState(customization.content || "");
    const [fromUnit, setFromUnit] = useState(customization.fromUnit);
    const [toUnit, setToUnit] = useState(customization.toUnit);
    const [dayId, setDayId] = useState(
        typeof customization.dayId === 'number' ? customization.dayId : (customization.dayId ? parseInt(customization.dayId) : 0)
    );
    const [errorMessage, setErrorMessage] = useState("");
    const [saveEnabled, setSaveEnabled] = useState(false);

    const [editCustomization, { loading: editLoading }] = useMutation(EDIT_TIMEPLAN_CUSTOMIZATION);
    const [removeCustomization, { loading: removeLoading }] = useMutation(REMOVE_TIMEPLAN_CUSTOMIZATION);

    // Get available time slots for "to" based on selected "from"
    const getAvailableToUnits = () => {
        return timeDescriptions.filter(time => time.index >= fromUnit);
    };

    // Validation effect
    useEffect(() => {
        let error = "";
        if (!content.trim()) {
            error = "Inhalt ist erforderlich.";
        } else if (fromUnit > toUnit) {
            error = "Von-Zeitslot muss vor oder gleich dem Bis-Zeitslot sein.";
        }

        setErrorMessage(error);
        setSaveEnabled(!error);
    }, [content, fromUnit, toUnit, dayId]);

    // Update toUnit when fromUnit changes to ensure it's not before fromUnit
    useEffect(() => {
        if (toUnit < fromUnit) {
            setToUnit(fromUnit);
        }
    }, [fromUnit]);

    const handleSave = async () => {
        if (!saveEnabled) return;

        try {
            await editCustomization({
                variables: {
                    customizationId: customization.id,
                    fromUnit: parseInt(fromUnit),
                    toUnit: parseInt(toUnit),
                    content: content.trim(),
                    dayId: parseInt(dayId)
                }
            });
            onClose();
        } catch (err) {
            console.error("Error saving customization:", err);
            setErrorMessage(`Fehler beim Speichern: ${err.message}`);
        }
    };

    const handleDelete = async () => {

        try {
            await removeCustomization({
                variables: {
                    customizationId: customization.id
                }
            });
            onClose();
        } catch (err) {
            console.error("Error deleting customization:", err);
            setErrorMessage(`Fehler beim Löschen: ${err.message}`);
        }
    };

    


    return (
        <Modal
            open={true}
            modalHeading="Personalisierung bearbeiten"
            primaryButtonText="Speichern"
            secondaryButtonText="Abbrechen"
            onRequestClose={onClose}
            onRequestSubmit={handleSave}
            primaryButtonDisabled={!saveEnabled || editLoading}
            closeButtonLabel="Schließen"
            size="md"
        >
            <div className="space-y-4">
                <TextInput
                    id="content"
                    labelText="Inhalt"
                    placeholder="Beschreibung der Personalisierung"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    required
                />


                <div className="flex space-x-4 w-full md:w-1/2">
                    <Select
                        id="dayId"
                        labelText="Wochentag"
                        value={dayId.toString()}
                        onChange={(e) => setDayId(parseInt(e.target.value))}
                    >
                        {dayNames.map((name, idx) => (
                            <SelectItem key={idx} value={idx.toString()} text={name} />
                        ))}
                    </Select>
                    <Select
                        id="fromUnit"
                        labelText="Von"
                        value={fromUnit.toString()}
                        onChange={(e) => setFromUnit(parseInt(e.target.value))}
                    >
                        {timeDescriptions.map((timeDesc) => (
                            <SelectItem
                                key={timeDesc.index}
                                value={timeDesc.index.toString()}
                                text={timeDesc.startTime}
                            />
                        ))}
                    </Select>

                    <Select
                        id="toUnit"
                        labelText="Bis"
                        value={toUnit.toString()}
                        onChange={(e) => setToUnit(parseInt(e.target.value))}
                    >
                        {getAvailableToUnits().map((timeDesc) => (
                            <SelectItem
                                key={timeDesc.index}
                                value={timeDesc.index.toString()}
                                text={timeDesc.endTime}
                            />
                        ))}
                    </Select>
                </div>

                {errorMessage && (
                    <p className="text-sm text-red-600">{errorMessage}</p>
                )}

                <div className="w-full flex justify-end">
                    <button
                        type="button"
                        onClick={handleDelete}
                        disabled={removeLoading}
                        className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                    >
                        {removeLoading ? "Löscht..." : "Personalisierung löschen"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
